import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { isEqual } from "lodash";
import classNames from "classnames";

// UI Components
import BootstrapTable from "react-bootstrap-table-next"; // https://react-bootstrap-table.github.io/react-bootstrap-table2/storybook/index.html
import paginationFactory from "react-bootstrap-table2-paginator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { toastr } from "react-redux-toastr";
import { Row, Col, Button, Container, Card, CardBody } from "reactstrap";

// State
import {
  getSeedLists,
  createSeedList,
  selectSeedList,
  updateSeedList,
  deleteSeedList,
} from "../../../redux/actions/adminActions";

// Custom Components
import { Group } from "../../../utils/logger";
import Loader from "../../../components/Loader";
import Header from "./Header";
import SeedModal from "./SeedModal";
import seed_table_schema from "./seedTableSchema";

// Globals
const RECORDS_PER_PAGE = "mbEonsRecordsPerPage";

class SeedList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      search_text: "",
      seedLists: props.seedLists,
      recordsPerPage: localStorage.getItem(RECORDS_PER_PAGE) || 50,

      // Modal
      modal: {
        type: "add",
        isOpen: false,
      },
    };

    this.LoggerGroup = new Group("SeedList", { disable: true });

    this.toggleModal = this.toggleModal.bind(this);
    this.onUserAction = this.onUserAction.bind(this);
    this.onSearchSeeds = this.onSearchSeeds.bind(this);
  }

  componentDidMount() {
    this.LoggerGroup.emit("Mounted").end();

    this.props.getSeedLists();
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const state = {};

    if (!isEqual(nextProps.seedLists, prevState.seedLists)) {
      state.seedLists = nextProps.seedLists;
    }

    return state;
  }

  onSearchSeeds({ target }) {
    // this.LoggerGroup.emit("Searching value", target.name, target.value);

    this.setState({ search_text: target.value });
  }

  onUserAction(type, seedList) {
    this.LoggerGroup.emit("User Action triggered", { dir: seedList });

    switch (type) {
      case "add":
        this.LoggerGroup.emit(`  [i] Adding Seed List`);
        this.props.selectSeedList({});
        this.toggleModal(true, this.props.createSeedList);
        break;

      case "edit":
        this.LoggerGroup.emit(`  [i] Editing Seed List`);
        this.props.selectSeedList(seedList);
        this.toggleModal(true, this.props.updateSeedList);
        break;

      case "delete":
        this.props.selectSeedList(seedList);
        this.LoggerGroup.emit(`  [i] Deleting Seed List`);
        toastr.confirm(`Are you sure you want to remove "${seedList.name_vch}"?`, {
          okText: `Delete "${seedList.name_vch}"`,
          onCancel: () =>
            this.LoggerGroup.emit(`Cancelled delete seedList[${seedList.seed_list_id_bint}]`),
          onOk: () => this.props.deleteSeedList(seedList),
        });
        break;

      default:
        return this.LoggerGroup.emit({ error: `Invalid user action type[${type}]` });
    }

    this.LoggerGroup.end();
  }

  toggleModal(show, applyChanges) {
    const { modal } = this.state;
    modal.isOpen = typeof show === "boolean" ? show : !modal.isOpen;
    modal.applyChanges = applyChanges;

    this.setState({ modal });
  }

  render() {
    const { onUserAction } = this;
    const { loading, selected } = this.props;
    const { search_text, seedLists, modal, recordsPerPage } = this.state;

    if (loading) {
      return <Loader />;
    }

    const search_text_regex = new RegExp(search_text, "i");
    const filtered_seed_lists = seedLists.filter((seedList) =>
      search_text_regex.test(seedList.name_vch)
    );

    const AddSeedList = (props) => (
      <Row className={classNames("mb-4", props.className)}>
        <Col className="text-right">
          <Button color="primary" onClick={() => onUserAction("add")}>
            <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
            Add Seed List
          </Button>
        </Col>
      </Row>
    );

    return (
      <Container fluid className="p-0">
        <Header onSearchSeeds={this.onSearchSeeds} />

        {modal.isOpen && (
          <SeedModal {...modal} toggleModal={this.toggleModal} selected={selected} />
        )}

        <AddSeedList />

        <Card>
          <CardBody>
            <BootstrapTable
              bootstrap4
              hover
              className="table-responsive"
              keyField="seed_list_id_bint"
              data={filtered_seed_lists}
              columns={seed_table_schema({ onUserAction })}
              bordered={false}
              pagination={paginationFactory({
                sizePerPage: Number(recordsPerPage),
                sizePerPageList: [5, 10, 25, 50, 75, 100],
                onSizePerPageChange: (sizePerPage) => {
                  localStorage.setItem(RECORDS_PER_PAGE, sizePerPage);
                },
              })}
              defaultSorted={[
                {
                  dataField: "name_vch",
                  order: "asc",
                },
              ]}
              rowClasses={(seedList) => {
                return seedList.seed_list_id_bint === selected.seed_list_id_bint
                  ? "table-info"
                  : null;
              }}
            />
          </CardBody>
        </Card>

        {filtered_seed_lists.length > 5 && <AddSeedList className="d-lg-none" />}
      </Container>
    );
  }
}

SeedList.propTypes = {
  errors: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  seedLists: PropTypes.array.isRequired,
  selected: PropTypes.object.isRequired,
  getSeedLists: PropTypes.func.isRequired,
  createSeedList: PropTypes.func.isRequired,
  selectSeedList: PropTypes.func.isRequired,
  updateSeedList: PropTypes.func.isRequired,
  deleteSeedList: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  errors: state.campaigns.errors,
  loading: state.admin.loading.seedLists,
  seedLists: state.admin.seedLists,
  selected: state.admin.selectedSeed,
});

export default connect(mapStateToProps, {
  getSeedLists,
  createSeedList,
  selectSeedList,
  updateSeedList,
  deleteSeedList,
})(SeedList);
